/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import { createListenerMiddleware, isRejected } from '@reduxjs/toolkit';
import { captureException as sentryCaptureException } from '@sentry/react';

import { analyticsTrack } from './analytics_utils';
import { textInputThunk } from '../../features/sharedcomponents/chat/chatSlice';
import { setActiveFhirSidebarMessageIndex } from '../../features/sharedcomponents/Fhir/fhirSlice';
import { RootState } from '../../app/store';

const listenerMiddleware = createListenerMiddleware<RootState>();

listenerMiddleware.startListening({
  matcher: isRejected,
  effect: async (action) => {
    if (!action.error) return;
    if (action.error.name === 'AbortError') return; // This is a fetch abort because the user navigated away or searched for something new etc

    // Note that sentry has a 100kb limit.  Be careful not to go over it
    const msg = `${action.type}: ${action.error.message}`.slice(0, 95000);
    sentryCaptureException(msg);
  },
});

listenerMiddleware.startListening({
  actionCreator: textInputThunk.fulfilled,
  effect: async (action, state) => {
    if (!action.payload) return;
    const { versions, text, audience, howActivated, searchId, search_source } = action.payload;
    const { user } = state.getState();
    if (!text) return;
    // In a smart-fhir context, use the encrypted mrn from the smart-fhir cookie where possible
    /* Before making changes to this, inform:
       Cynthia.Walsh@providence.org
       Prateek.Fulsundar@providence.org

       If you modify this, please update the documentation at:
       https://pearl-project.atlassian.net/wiki/spaces/PearlArchitecture/pages/158662658/Mixpanel+and+Pendo

       This search event is used to track when any search is trigger,
       by the user typing in a search in the text box, or by clicking
       a link.

        term: the search term.  e.g. "my heart hurts"
        persona: the persona of the user.  e.g. "Provider"
        careCategory: the care category of the user.  e.g. "Outpatient"
        patientGroup: the patient group of the user.  e.g. "Adult and Peds"
        howActivated: how the search was activated. One of: userTyped, urlLink, algorithmClick, teamsBot
        numResults: the number of results returned by the search. This is initially capped to 20
        searchId: the id of the search.  This is to connect multiple search events.  The subsequent search_no_result and search_multiple_results_then_click events will have the same search_id
        search_source: the type of search used "pg" for legacy search and "algolia" when using algolia search
    */
    analyticsTrack(
      'search',
      {
        term: text,
        persona: audience.persona || 'Provider',
        careCategory: audience.careCategory || 'Outpatient',
        patientGroup: audience.patientGroup || 'Adult and Peds',
        howActivated, // one of: userTyped, urlLink, algorithmClick, teamsBot
        numResults: versions?.length,
        searchId,
        search_source,
      },
      user?.pearlUserInfo
    );
  },
});

listenerMiddleware.startListening({
  predicate: (_action, currentState, previousState) => {
    const previousLastMessage = previousState.chat.messages[previousState.chat.messages.length - 1];
    const currentLastMessage = currentState.chat.messages[currentState.chat.messages.length - 1];
    return previousLastMessage !== currentLastMessage && currentState.fhir.activeSidebarMessageIndex !== undefined;
  },
  effect: async (_action, listenerApi) => {
    listenerApi.dispatch(setActiveFhirSidebarMessageIndex(undefined));
  },
});

export default listenerMiddleware;
